import React, {Component} from 'react';
import PropTypes from 'prop-types';
import PreferencePanel from '../PreferencePanel/PreferencePanel';
import CurrencySelector from '../CurrencySelector/CurrencySelector';
import CountryLanguageSelector from '../CountryLanguageSelector/CountryLanguageSelector';
import {ButtonPrimary} from '@homeaway/react-buttons';
import Modal from '@homeaway/react-modal';
import triggerEdapEvent from '../../utils/edap';
import './GlobalizationSelector.less';

class GlobalizationSelector extends Component {
    static propTypes = {
        /* country language currency details for current site */
        currencyCode: PropTypes.string,
        currencyName: PropTypes.string,
        currencySymbol: PropTypes.string,
        countryCode: PropTypes.string,
        languageCode: PropTypes.string,
        renderCurrencySelector: PropTypes.bool,
        renderCountryLanguageSelector: PropTypes.bool,
        countryLanguageSelector: PropTypes.shape({
            countries: PropTypes.arrayOf(
                PropTypes.shape({
                    countryCode: PropTypes.string,
                    countryName: PropTypes.string,
                    siteUrl: PropTypes.string,
                    languages: PropTypes.arrayOf(
                        PropTypes.shape({
                            languageCode: PropTypes.string,
                            languageName: PropTypes.string
                        })
                    )
                })
            )
        }),
        currencySelector: PropTypes.shape({
            currencies: PropTypes.arrayOf(
                PropTypes.shape({
                    code: PropTypes.string,
                    symbol: PropTypes.string,
                    name: PropTypes.string
                })
            )
        }),
        /* text to display on the selector */
        translations: PropTypes.shape({
            globalTitle: PropTypes.string,
            currency: PropTypes.string,
            country: PropTypes.string,
            chooseCurrency: PropTypes.string,
            chooseCountry: PropTypes.string,
            chooseLanguage: PropTypes.string,
            update: PropTypes.string,
            change: PropTypes.string
        }),
        onGlobalSelectorChange: PropTypes.func,
    }

    static defaultProps = {
        currencyCode: 'USD',
        currencyName: 'US Dollar',
        currencySymbol: 'US$',
        countryCode: 'us',
        languageCode: 'en_US',
        renderCurrencySelector: false,
        renderCountryLanguageSelector: true,
        translations: {
            globalTitle: 'Regional settings',
            chooseCurrency: 'Currency',
            chooseCountry: 'Country',
            chooseLanguage: 'Language',
            currencyDisclaimer: "Please note: You will still be charged in the owner's currency.",
            update: 'Update',
            change: 'Change'
        },
        onGlobalSelectorChange: () => {}
    }

    /* istanbul ignore next */
    constructor(props) {
        super(props);
        this.state = this.getStateDataByProps(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.setState(this.getStateDataByProps(nextProps));
    }

    handleOpen = () => {
        this.setState({
            isOpen: true,
            currencyInSelector: this.state.currencyInSelector
        });
    }

    handleClose = () => {
        let languages = this.getLanguagesByCountryCode(this.props.countryLanguageSelector, this.state.countryCodeInPreference);
        this.setState({
            isOpen: false,
            countryCodeInSelector: this.state.countryCodeInPreference,
            languages,
            languageCodeInSelector: this.state.languageCodeInPreference,
            currencyInSelector: this.state.currencyInPreference
        });

        if (this.state.renderCurrencySelector) {
            this.setState({
                currencyInSelector: this.state.currencyInPreference
            });
        }
    }

    handleModalOpen = () => {
        triggerEdapEvent({
            eventaction: 'regional settings modal opened'
        });

        if (this.state.renderCountryLanguageSelector) {
            if (!this.state.languageSelectorOnly) {
                triggerEdapEvent({
                    eventaction: 'display country dropdown seen'
                });
            }

            triggerEdapEvent({
                eventaction: 'display language dropdown seen'
            });
        }

        if (this.state.renderCurrencySelector) {
            triggerEdapEvent({
                eventaction: 'display currency dropdown seen'
            });
        }
    }

    handleModalClose() {
        triggerEdapEvent({
            eventaction: 'regional settings modal closed'
        });
    }

    handleUpdate = () => {
        if (this.state.renderCurrencySelector) {
            this.setState({
                currencyInPreference: this.getCurrencyFromCurrencyCode(this.props.currencySelector, this.state.currencyCodeSelected),
                isOpen: false
            });

            triggerEdapEvent({
                eventaction: 'display currency updated',
                eventlabel: this.state.currencyCodeSelected
            });
        }

        if (this.state.renderCountryLanguageSelector) {
            let selectedCountry = this.props.countryLanguageSelector.countries.filter((country) => country.countryCode === this.state.countryCodeInSelector)[0];
            let languageName = selectedCountry.languages.filter((language) => language.languageCode === this.state.languageCodeInSelector)[0].languageName;
            this.setState({
                countryCodeInPreference: this.state.countryCodeInSelector,
                languageCodeInPreference: this.state.languageCodeInSelector,
                languageName,
                isOpen: false
            });

            if (!this.state.languageSelectorOnly) {
                triggerEdapEvent({
                    eventaction: 'display country updated',
                    eventlabel: this.state.countryCodeInPreference
                });
            }

            triggerEdapEvent({
                eventaction: 'display language updated',
                eventlabel: this.state.languageCodeInSelector
            });

            this.props.onGlobalSelectorChange(this.state.languageCodeInSelector, selectedCountry.siteUrl, this.state.currencyCodeSelected);
        }

        this.props.onGlobalSelectorChange(this.state.languageCodeInSelector, null, this.state.currencyCodeSelected);
    }

    handleLanguageSelectorChange = (languageCodeInSelector) => {
        this.setState({languageCodeInSelector});
    }

    handleCountrySelectorChange = (countryCodeInSelector) => {
        let languages = this.getLanguagesByCountryCode(this.props.countryLanguageSelector, countryCodeInSelector);
        this.setState({
            countryCodeInSelector,
            languages,
            languageCodeInSelector: languages[0].languageCode
        });
    }

    handleCurrencySelectorChange = (currencyCodeSelected) => {
        this.setState({
            currencyInSelector: this.getCurrencyFromCurrencyCode(this.props.currencySelector, currencyCodeSelected),
            currencyCodeSelected
        });
    }

    getCurrencyFromCurrencyCode(currencySelector, currencyCode) {
        if (!currencySelector) {
            return null;
        }
        return currencySelector.currencies.filter((currency) => currencyCode === currency.code)[0];
    }

    getStateDataByProps(props) {
        let languages = this.getLanguagesByCountryCode(props.countryLanguageSelector, props.countryCode);
        let supportedLanguages = languages ? languages.filter((language) => props.languageCode === language.languageCode) : null;
        let languageName = supportedLanguages && supportedLanguages.length ? supportedLanguages[0].languageName : null;
        let currencyCode = this.getCurrencyFromCurrencyCode(props.currencySelector, props.currencyCode);
        return {
            isOpen: false,
            languages,
            countryCodeInSelector: props.countryCode,
            languageCodeInSelector: props.languageCode,
            countryCodeInPreference: props.countryCode,
            languageCodeInPreference: props.languageCode,
            languageName,
            currencyInSelector: currencyCode,
            currencyInPreference: currencyCode,
            currencyCodeSelected: props.currencyCode,
            renderCountryLanguageSelector: props.renderCountryLanguageSelector && !!props.countryLanguageSelector && !!languageName,
            renderCurrencySelector: props.renderCurrencySelector && !!props.currencySelector && !!currencyCode,
            languageSelectorOnly: props.countryLanguageSelector && (props.languageSelectorOnly || props.countryLanguageSelector.countries.length === 1)
        };
    }

    getLanguagesByCountryCode(countryLanguageSelector, countryCode) {
        if (!countryLanguageSelector) {
            return null;
        }
        let supportedCountries = countryLanguageSelector.countries.filter((country) => country.countryCode === countryCode);
        if (supportedCountries && supportedCountries.length) {
            return countryLanguageSelector.countries.filter((country) => country.countryCode === countryCode)[0].languages;
        }
        return null;
    }

    render() {
        const {translations, countryLanguageSelector, currencySelector} = this.props;
        let renderPreference = this.state.renderCurrencySelector || this.state.renderCountryLanguageSelector;
        return (
            <div className="GlobalizationSelector">
                {renderPreference &&
                <PreferencePanel
                    currency={this.state.currencyInPreference}
                    countryCode={this.state.countryCodeInPreference}
                    locale={this.state.languageCodeInPreference}
                    languageName={this.state.languageName}
                    onClick={this.handleOpen}
                    change={translations.change}
                    languageSelectorOnly={this.state.languageSelectorOnly}
                    renderCountryLanguageSelector={this.state.renderCountryLanguageSelector}
                    renderCurrencySelector={this.state.renderCurrencySelector}
                />}
                <Modal
                    id="globalization-selector-modal"
                    isOpen={this.state.isOpen}
                    onClose={this.handleClose}
                    title={translations.globalTitle}
                    isWorkflow
                    footer={<ButtonPrimary onClick={this.handleUpdate} label={translations.update} block/>}
                    onModalOpen={this.handleModalOpen}
                    onModalClose={this.handleModalClose}
                >
                    {this.state.renderCountryLanguageSelector &&
                    <CountryLanguageSelector
                        translations={translations}
                        countries={countryLanguageSelector.countries}
                        languages={this.state.languages}
                        countryCode={this.state.countryCodeInSelector}
                        languageCode={this.state.languageCodeInSelector}
                        languageSelectorOnly={this.state.languageSelectorOnly}
                        handleCountrySelectorChange={this.handleCountrySelectorChange}
                        handleLanguageSelectorChange={this.handleLanguageSelectorChange}
                    />}

                    {this.state.renderCurrencySelector &&
                    <CurrencySelector
                        title={translations.chooseCurrency}
                        currency={this.state.currencyInSelector}
                        disclaimer={translations.currencyDisclaimer}
                        currencyList={currencySelector.currencies}
                        onChange={this.handleCurrencySelectorChange}
                    />}

                </Modal>
            </div>
        );
    }
}

export default GlobalizationSelector;
